var __awaiter = (this && this.__awaiter) || function (thisArg, _arguments, P, generator) {
    function adopt(value) { return value instanceof P ? value : new P(function (resolve) { resolve(value); }); }
    return new (P || (P = Promise))(function (resolve, reject) {
        function fulfilled(value) { try { step(generator.next(value)); } catch (e) { reject(e); } }
        function rejected(value) { try { step(generator["throw"](value)); } catch (e) { reject(e); } }
        function step(result) { result.done ? resolve(result.value) : adopt(result.value).then(fulfilled, rejected); }
        step((generator = generator.apply(thisArg, _arguments || [])).next());
    });
};
import * as THREE from 'three';
import { World } from '../worlds/World';
import * as ObjectCreation from '../ObjectCreation';
import * as TextCreation from '../TextCreation';
import { OrbFactory } from '../../../orbs/OrbFactory';
import { registerScenePolicy } from '../../../core/ScenePolicies';
import { global } from '../../../core/GlobalState';
import { Orb } from '../../../orbs/Orb';
import * as DemoSceneTools from '../DemoSceneTools';
import { OrbConnectionManager } from '../../connections/OrbConnectionManager';
import { createSimpleSatelliteDish } from '../generializedMachinery/SatelliteDish';
import * as ImageLoading from '../ImageLoading';
import * as SystemAPI from '../../../core/SystemAPI';
import { buildRingGallery } from './RingGallery';
import { buildGiantRainbowCylinderStack } from '../DynamicSystemDesignModel';
const startSprayPosition = new THREE.Vector3(2000, 5000, -2000);
function createCodeBillboard(title, code, position) {
    const width = 2048;
    const height = 1024;
    const canvas = document.createElement('canvas');
    canvas.width = width;
    canvas.height = height;
    const ctx = canvas.getContext('2d');
    if (!ctx) {
        throw new Error('Unable to obtain 2D context for code billboard');
    }
    ctx.clearRect(0, 0, width, height);
    ctx.fillStyle = 'rgba(8, 12, 22, 0.88)';
    ctx.fillRect(0, 0, width, height);
    ctx.strokeStyle = 'rgba(98, 179, 255, 0.45)';
    ctx.lineWidth = 12;
    ctx.strokeRect(80, 80, width - 160, height - 160);
    ctx.fillStyle = '#62b3ff';
    ctx.font = 'bold 120px "IBM Plex Mono", monospace';
    ctx.fillText(title, 140, 210);
    ctx.fillStyle = '#f1f5ff';
    ctx.font = '84px "IBM Plex Mono", monospace';
    const lines = code.trim().split('\n');
    const lineHeight = 110;
    lines.forEach((line, index) => {
        ctx.fillText(line, 140, 320 + index * lineHeight);
    });
    const texture = new THREE.CanvasTexture(canvas);
    texture.anisotropy = 4;
    texture.needsUpdate = true;
    const material = new THREE.MeshBasicMaterial({ map: texture, transparent: true });
    const geometry = new THREE.PlaneGeometry(1400, 700);
    const mesh = new THREE.Mesh(geometry, material);
    mesh.position.copy(position);
    mesh.lookAt(new THREE.Vector3(position.x, position.y + 200, position.z - 800));
    return mesh;
}
export function addDemoContent(scene) {
    return __awaiter(this, void 0, void 0, function* () {
        yield DemoSceneTools.addTextDemoGallery(scene);
        DemoSceneTools.createMiniOrbGallery(scene);
        DemoSceneTools.shapeTests(scene);
        yield addServiceIconRow(scene);
        /*
        await DemoSceneTools.testLoadImageSprite(
          scene,
          "https://api.orbscape.io/proxyimage?url=https://www.desmos.com/calc_thumbs/production/hd5mjyoo9t.png",
          new THREE.Vector3(100, 100, 0)
        );
        */
    });
}
export function addServiceIconRow(scene) {
    return __awaiter(this, void 0, void 0, function* () {
        // 1) Pull raw data
        const rawIconsData = yield SystemAPI.getServiceIcons();
        // 2) Normalize
        const entries = [];
        if (Array.isArray(rawIconsData)) {
            for (const item of rawIconsData) {
                if (!item || typeof item !== 'object')
                    continue;
                const nameVal = item.name;
                const ipVal = item.ip;
                const portVal = item.port;
                const iconVal = item.icon;
                if (typeof nameVal !== 'string' || typeof iconVal !== 'string')
                    continue;
                entries.push({
                    name: nameVal.trim(),
                    icon: iconVal.trim(),
                    ip: typeof ipVal === 'string'
                        ? (ipVal.toLowerCase() === 'null' ? null : ipVal.trim())
                        : null,
                    port: typeof portVal === 'string'
                        ? (portVal.toLowerCase() === 'null' ? null : portVal.trim())
                        : null,
                });
            }
        }
        else {
            console.warn('[addServiceIconRow] Unexpected response from getServiceIcons():', rawIconsData);
        }
        if (entries.length === 0) {
            console.warn('[addServiceIconRow] No valid service icons to render.');
            return;
        }
        // 3) Row layout config
        const startZ = -1000;
        const startY = 200;
        const x = 1700;
        const spacing = 120;
        // 4) Spawn sprites (shared helper)
        for (let i = 0; i < entries.length; i++) {
            const service = entries[i];
            const proxiedUrl = `https://api.orbscape.io/proxyimage?url=${encodeURIComponent(service.icon)}`;
            const position = new THREE.Vector3(x, startY, startZ + i * spacing);
            try {
                yield ImageLoading.attachMaskedSprite(scene, proxiedUrl, {
                    maskStyle: 'roundedSquare',
                    size: 192,
                    radius: 28,
                    scale: 100,
                    position, // place where you want it
                    // SVGs will default to white; override here if you want a brand color
                    // svgColor: '#00D8FF'
                });
            }
            catch (err) {
                console.warn('[addServiceIconRow] failed to load sprite for', service.name, err);
            }
        }
    });
}
export function addConnectionRows(scene) {
    var _a, _b, _c, _d;
    const cm = OrbConnectionManager.getInstance();
    // Layout: 2 rows within ±1000 on X/Z, then shift everything +3000 on X
    const xShift = 3000;
    const xDelta = -13000; // << shift whole block left by 13,000
    const maxX = 1000;
    const maxZ = 1000;
    const y = 160;
    const zA = 600; // Row A (top)
    const zB = -600; // Row B (bottom)
    const margin = 120;
    const profiles = makeProfiles();
    const count = profiles.length;
    const span = maxX * 2 - margin * 2;
    const step = count > 1 ? span / (count - 1) : 0;
    const rowA = [];
    const rowB = [];
    for (let i = 0; i < count; i++) {
        const x = -maxX + margin + i * step + xShift + xDelta; // << apply shift
        const a = OrbFactory.createOrbByType('SPHERE_ORB', `A${i + 1}`);
        a.setPosition(new THREE.Vector3(x, y, zA));
        a.addToScene(scene);
        const b = OrbFactory.createOrbByType('SPHERE_ORB', `B${i + 1}`);
        b.setPosition(new THREE.Vector3(x, y, zB));
        b.addToScene(scene);
        rowA.push(a);
        rowB.push(b);
    }
    // Apply unique connection profile to each A-orb, connect A -> B, add explanation text behind A
    for (let i = 0; i < count; i++) {
        const a = rowA[i];
        const b = rowB[i];
        const c = profiles[i];
        const profileName = `conn_${(i + 1).toString().padStart(2, '0')}`;
        if (!a.orbConfig.edgeProfiles)
            a.orbConfig.edgeProfiles = {};
        a.orbConfig.edgeProfiles[profileName] = Object.assign(Object.assign({}, c.profile), { name: profileName });
        cm.connect({
            a,
            b,
            type: 'edge',
            profileName,
            labelText: (_b = (_a = c.profile.label) === null || _a === void 0 ? void 0 : _a.text) !== null && _b !== void 0 ? _b : `${i + 1}`,
        });
        // Explanation "section" behind each A orb (slightly behind on +Z)
        const aPos = (_d = (_c = a.group) === null || _c === void 0 ? void 0 : _c.position) !== null && _d !== void 0 ? _d : new THREE.Vector3();
        const infoPos = new THREE.Vector3(aPos.x, aPos.y + 110, aPos.z + 140);
        const sprite = TextCreation.createTextSprite(`${(i + 1).toString().padStart(2, '0')}`, 2, infoPos);
        scene.add(sprite);
        TextCreation.startSpeedTextEffect(sprite, c.explain, {
            wpm: 520,
            font: '34px Arial',
            fillStyle: '#6699CC',
            outlineStyle: '#095ceb',
            outlineWidth: 3,
        });
    }
    // ---- helpers ----
    function dm(near, far, min, max) {
        return { near, far, min, max };
    }
    function makeProfiles() {
        const out = [];
        // BASICS
        out.push({
            profile: { style: { color: 0x2196f3, widthPx: 4 }, curve: { mode: 'straight' } },
            explain: 'Basic straight line; default width 4; good baseline.',
        });
        out.push({
            profile: { style: { color: 0xb0bec5, widthPx: 2, opacity: 0.9 }, curve: { mode: 'straight' } },
            explain: 'Thin straight line; subtle visual weight.',
        });
        out.push({
            profile: { style: { color: 0x00e676, widthPx: 10, opacity: 1.0 }, curve: { mode: 'straight' } },
            explain: 'Bold straight line; strong emphasis for primary links.',
        });
        // DASHED
        out.push({
            profile: { style: { color: 0x90a4ae, widthPx: 4, dashed: true, dashScale: 1.0 }, curve: { mode: 'straight' } },
            explain: 'Dashed thin; suggests optional/derived relation.',
        });
        out.push({
            profile: { style: { color: 0x607d8b, widthPx: 8, dashed: true, dashScale: 1.25, opacity: 0.95 } },
            explain: 'Dashed thick; standout but still non-solid.',
        });
        // ARROWS
        out.push({
            profile: { style: { color: 0x4caf50, widthPx: 6 }, arrows: { end: true, size: 14 } },
            explain: 'Directional (A→B) with end arrow; causal or flow.',
        });
        out.push({
            profile: { style: { color: 0xff7043, widthPx: 6 }, arrows: { start: true, size: 12 } },
            explain: 'Directional (B→A) with start arrow; reverse emphasis.',
        });
        out.push({
            profile: { style: { color: 0xff9800, widthPx: 6 }, arrows: { twoSided: true, size: 12 } },
            explain: 'Two-sided arrows; bidirectional communication.',
        });
        // LABELS
        out.push({
            profile: { style: { color: 0x26c6da, widthPx: 5 }, label: { text: 'relates-to', atT: 0.50, spriteScale: 28 } },
            explain: 'Mid-span label; neutral relation text.',
        });
        out.push({
            profile: { style: { color: 0x8d6e63, widthPx: 5 }, label: { text: 'producer', atT: 0.18, spriteScale: 28 }, arrows: { end: true, size: 12 } },
            explain: 'Label near A; “producer” hint + arrow toward B.',
        });
        out.push({
            profile: { style: { color: 0x7e57c2, widthPx: 5 }, label: { text: 'consumer', atT: 0.85, spriteScale: 28 } },
            explain: 'Label near B; “consumer” hint; downstream.',
        });
        // CURVES (QUADRATIC / CUBIC)
        out.push({
            profile: { style: { color: 0x9575cd, widthPx: 5 }, curve: { mode: 'quadratic', bend: 0.18, segments: 24 } },
            explain: 'Quadratic gentle bend; slight arc for clarity.',
        });
        out.push({
            profile: { style: { color: 0x673ab7, widthPx: 5 }, curve: { mode: 'quadratic', bend: 0.35, segments: 28 } },
            explain: 'Quadratic strong bend; dramatic separation.',
        });
        out.push({
            profile: { style: { color: 0xffb74d, widthPx: 5 }, curve: { mode: 'cubic', bend: 0.28, segments: 28 } },
            explain: 'Cubic gentle; smoother curvature endpoints.',
        });
        out.push({
            profile: { style: { color: 0xffa726, widthPx: 5 }, curve: { mode: 'cubic', bend: 0.45, segments: 32 } },
            explain: 'Cubic strong; pronounced arc for routing around nodes.',
        });
        // OFFSETS (SURFACE EXTRA OFFSETS)
        out.push({
            profile: { style: { color: 0xe57373, widthPx: 5, extraOffsetStart: 24, extraOffsetEnd: 24 } },
            explain: 'Small endpoint offsets; avoid visual overlap with orbs.',
        });
        out.push({
            profile: { style: { color: 0xef5350, widthPx: 6, extraOffsetStart: 60, extraOffsetEnd: 60 } },
            explain: 'Large endpoint offsets; keep line clear of thick halos.',
        });
        // DISTANCE-RESPONSIVE
        out.push({
            profile: { style: { color: 0x42a5f5, widthPx: 5, distanceStyle: { widthPx: dm(300, 1200, 2, 10) } } },
            explain: 'Width increases when closer; proximity emphasis.',
        });
        out.push({
            profile: { style: { color: 0xab47bc, widthPx: 6, opacity: 1.0, distanceStyle: { opacity: dm(350, 1300, 0.25, 1.0) } } },
            explain: 'Opacity fades with distance; de-emphasize far links.',
        });
        out.push({
            profile: { style: { color: 0x29b6f6, widthPx: 5, opacity: 0.95, distanceStyle: { widthPx: dm(300, 1300, 2, 9), opacity: dm(300, 1300, 0.3, 1.0) } } },
            explain: 'Width + opacity both respond to distance.',
        });
        // ELASTIC (PHYSICS)
        out.push({
            profile: { style: { color: 0x66bb6a, widthPx: 6 }, elastic: { enabled: true, rest: 'surface', stiffness: 2.0, damping: 1.2, maxForce: 1000 }, arrows: { end: true, size: 12 } },
            explain: 'Elastic soft; gentle spring toward rest length.',
        });
        out.push({
            profile: { style: { color: 0x26a69a, widthPx: 6, dashed: true }, elastic: { enabled: true, rest: 'surface', stiffness: 5.0, damping: 2.0, maxForce: 1600 } },
            explain: 'Elastic medium + dashed; balanced restoring force.',
        });
        out.push({
            profile: { style: { color: 0x00897b, widthPx: 7 }, elastic: { enabled: true, rest: 'surface', stiffness: 12.0, damping: 2.8, maxForce: 3000 }, arrows: { twoSided: true, size: 11 } },
            explain: 'Elastic stiff + two-sided arrows; taut coupling.',
        });
        out.push({
            profile: { style: { color: 0x9ccc65, widthPx: 6 }, curve: { mode: 'cubic', bend: 0.35, segments: 28 }, elastic: { enabled: true, rest: 'surface', stiffness: 3.0, damping: 1.4, maxForce: 1400 } },
            explain: 'Elastic + cubic; spring with graceful curvature.',
        });
        // COMBINATIONS / HIGHLIGHTS
        out.push({
            profile: { style: { color: 0xffca28, widthPx: 5 }, curve: { mode: 'cubic', bend: 0.42, segments: 28 }, arrows: { twoSided: true, size: 10 }, label: { text: 'bidirectional', atT: 0.65, spriteScale: 26 } },
            explain: 'Cubic + two-sided + label; rich, descriptive link.',
        });
        out.push({
            profile: { style: { color: 0x455a64, widthPx: 6, dashed: true }, arrows: { end: true, size: 12 }, label: { text: 'async', atT: 0.55, spriteScale: 24 } },
            explain: 'Dashed + arrow + “async”; non-blocking flow.',
        });
        out.push({
            profile: { style: { color: 0x00bcd4, widthPx: 7, extraOffsetStart: 36, extraOffsetEnd: 36 }, label: { text: 'highlight', atT: 0.40, spriteScale: 26 } },
            explain: 'Bold with offsets + label; clear and readable near nodes.',
        });
        return out;
    }
}
export function addBillboardTests(scene) {
    const snippets = [
        {
            title: 'Portal Prototype',
            code: `const orb = OrbFactory.createOrbByType('SYSTEM_ORB', 'System Portal');
orb.orbConfig.isPortal = true;
orb.orbConfig.portalTargetSceneName = 'system';
orb.addToScene(scene);`,
            position: new THREE.Vector3(-1800, 520, 4600),
        },
        {
            title: 'Connection Demo',
            code: `const cm = OrbConnectionManager.getInstance();
cm.connect({ a: source, b: target, type: 'edge' });
cm.animateEdge('pulse', { speed: 1.2 });`,
            position: new THREE.Vector3(0, 520, 4600),
        },
        {
            title: 'Shader Sketch',
            code: `uniform float time;
vec3 ripple(vec3 pos) {
  float wave = sin(pos.x * 0.06 + time);
  return vec3(0.4, 0.7, 1.0) + wave * 0.2;
}`,
            position: new THREE.Vector3(1800, 520, 4600),
        },
    ];
    snippets.forEach(snippet => {
        const board = createCodeBillboard(snippet.title, snippet.code, snippet.position);
        scene.add(board);
    });
}
const sprayDefaults = {
    baseVelocity: 5,
    sprayCoefficient: 2100,
    timeGap: () => global.baseTimeGap, // ensure we read the current value
};
const fetcherByRegion = {
    'Domain Region': { fetcher: 'domainOrbs', label: 'DOMAIN_ORB' },
    'Network Gateway Region': { fetcher: 'networkGatewayOrbs', label: 'NETWORK_GATEWAY_ORB' },
    'Container Endpoint Region': { fetcher: 'endpointOrbs', label: 'CONTAINER_ENDPOINT_ORB' },
    'Docker Container Region': { fetcher: 'dockerOrbs', label: 'DOCKER_CONTAINER_ORB' },
    'Container Image Region': { fetcher: 'registryOrbs', label: 'CONTAINER_IMAGE_ORB' },
    'GitHub Repository Region': { fetcher: 'githubOrbs', label: 'GITHUB_ORB' },
    'Code Region': { fetcher: 'textObjectOrbs', label: 'TEXT_OBJECT_ORB' },
    'Database Object Region': { fetcher: 'databaseObjectOrbs', label: 'DATABASE_OBJECT_ORB' },
    'Computer Region': { fetcher: 'dockerSwarmOrbs', label: 'COMPUTER_ORB' },
};
function buildDevelopmentWorld(context) {
    const { scene } = context;
    scene.background = new THREE.Color(0xcaf1fc);
    scene.fog = new THREE.Fog(0xeaf1ff, 0, 26000);
    const { mirror, tintOverlay } = ObjectCreation.createMirrorFloor(1600, {
        baseColor: 0xe2e8ff,
        y: 0.05,
        dpr: 1.5,
        textureScale: 0.85,
        clipBias: 0.002,
    });
    const tintMaterial = tintOverlay.material;
    tintMaterial.opacity = 0.24;
    tintMaterial.color = new THREE.Color(0x6d8bff);
    scene.add(mirror);
    scene.add(tintOverlay);
    //scene.add(ObjectCreation.createWorldAxis(20000, 20000, 1000))
    const devAxis = ObjectCreation.createWorldAxis(20000, // axisSize
    20000, // tickSize (example)
    1000, // tickDistance (example)
    {
        axisColor: 0xc1c8f7,
        verticalOffset: 0.01,
        showGrid: true,
        gridSize: 40000,
        gridDivisions: 240,
        gridPrimaryColor: 0xb9cfff,
        gridSecondaryColor: 0x7d91f0,
        gridOpacity: 0.32,
        gridYOffset: 0.06,
    });
    scene.add(devAxis);
    const headline = TextCreation.createTextSprite('Development Sandbox', 3.4, new THREE.Vector3(0, 540, -680));
    const subtitle = TextCreation.createTextSprite('Experiments & Archived Snippets', 1.8, new THREE.Vector3(0, 430, -680));
    scene.add(headline);
    scene.add(subtitle);
    const ambient = new THREE.HemisphereLight(0xcfe1ff, 0x8aa0ff, 0.85);
    const rimLight = new THREE.DirectionalLight(0xffffff, 0.6);
    rimLight.position.set(-2600, 3400, 1800);
    const fill = new THREE.DirectionalLight(0xaec8ff, 0.35);
    fill.position.set(2600, 2800, -1600);
    scene.add(ambient);
    scene.add(rimLight);
    scene.add(fill);
    addBillboardTests(scene);
    //Actual code of mine to add demo content
    addDemoContent(scene);
    addConnectionRows(scene);
    //TODO migrate to a whole suite of machanical looking objectsl iek this. I am going form simply constructed objects with a Satisfactory-like aesthetic
    const dish = createSimpleSatelliteDish({
        radius: 700,
        depth: 160,
        thickness: 40,
        tiltDeg: 25,
        useAccent: true,
        colors: { metal: 0x9aa4b1, dark: 0x4a4f57, accent: 0xff7f2a },
    });
    dish.position.set(-6000, 0, 5000);
    scene.add(dish);
    buildRingGallery(scene);
    // part of orb call refactoring effort, but I am still seeking an even cleaner way, you can use this an a partial example
    // todo find a cleaner way to do this (monadic dot chaining?), then convert all other orb creations to that format
    //let bOrb: Orb = OrbFactory.createOrbByType("BLACKHOLE", "");
    //bOrb.setPosition(startSprayPosition);
    //bOrb.addToScene(scene);
    /*
    const sphereOrb = OrbFactory.createOrbByType("SPHERE_ORB", "∞");
    sphereOrb.setPosition(new THREE.Vector3(0, 2400, 1000));
    const group = sphereOrb.createOrb();
    const arrangement = ObjectCreation.createOrbSphericalArrangement(
        1000, 400, new THREE.Vector3(-5000, 2400, -5000), group
    );
    arrangement.forEach(child => scene.add(child));
    */
    buildGiantRainbowCylinderStack(scene, new THREE.Vector3(12000, 1300, 0));
    // --- Double-layer Rounded Cylinder Orb (with explicit geometry + transparency) ---
    const doubleCylinderConfig = {
        orbType: 'DOUBLE_ROUNDED_CYLINDER',
        radius: 180,
        // global material defaults (per-orb)
        defaultTransparent: true,
        //defaultOpacity: 0.9,           // slightly see-through
        defaultLayerSide: THREE.BackSide,
        defaultLayerScaleFactor: 0.08,
        textColor: 0xf0f0f0,
        textOutlineColor: 0xf0f0f0,
        layers: [
            {
                shape: 'roundedCylinder',
                color: 0x3496d3,
                scaleFactor: 0.00,
                // per-layer override (keeps global transparency)
                geometryParams: {
                    shape: 'roundedCylinder',
                    height: 220,
                    bevelRadius: 22,
                    radialSegments: 72,
                    bevelSegments: 14,
                },
                // optional per-layer material override example:
                // transparent: true, opacity: 0.85,
            },
            {
                shape: 'roundedCylinder',
                color: 0xf0f0f0,
                geometryParams: {
                    shape: 'roundedCylinder',
                    height: 220,
                    bevelRadius: 22,
                    radialSegments: 72,
                    bevelSegments: 14,
                },
            },
        ],
    };
    const doubleCylinderOrb = new Orb(doubleCylinderConfig, {}, 'Double Cylinder');
    doubleCylinderOrb.setPosition(new THREE.Vector3(-2400, 900, 900));
    doubleCylinderOrb.addToScene(scene);
    // --- Double-layer Rounded Rectangle Orb (explicit L/W/H/corner + wireframe inner) ---
    const doubleRectangleConfig = {
        orbType: 'DOUBLE_ROUNDED_RECT',
        radius: 180,
        defaultLayerSide: THREE.BackSide,
        defaultLayerScaleFactor: 0.08,
        textColor: 0xf0f0f0,
        textOutlineColor: 0xf0f0f0,
        layers: [
            {
                shape: 'roundedRectangle',
                color: 0xff7f2a,
                scaleFactor: 0.00,
                geometryParams: {
                    shape: 'roundedRectangle',
                    length: 360,
                    width: 220,
                    height: 90,
                    cornerRadius: 28,
                    bevelSegments: 14,
                },
                // show how per-layer transparency can differ:
                transparent: true,
                //opacity: 0.85,
                // or experiment with wireframe:
                // wireframe: true,
            },
            {
                shape: 'roundedRectangle',
                color: 0xcfd8dc,
                geometryParams: {
                    shape: 'roundedRectangle',
                    length: 360,
                    width: 220,
                    height: 90,
                    cornerRadius: 28,
                    bevelSegments: 14,
                },
            },
        ],
    };
    const doubleRectangleOrb = new Orb(doubleRectangleConfig, {}, 'Double Rectangle');
    doubleRectangleOrb.setPosition(new THREE.Vector3(-2800, 900, 1500));
    doubleRectangleOrb.addToScene(scene);
    //putting this here for reference:
    /*
    actionRegistry.invoke('connectByProfiles', {
      fromType: 'DOCKER_CONTAINER_ORB',
      profileLabel: 'Connect Containers to Endpoints'
    });
    actionRegistry.invoke('connectByProfiles', {
      fromType: 'DOCKER_SWARM_ORB',
      profileLabel: 'Connect Swarm to Containers'
    });
    */
    // I Want some dedicated place to run the webscraper from, and maybe have the contianer orb hook in to it
    //TODO!! Add in a demo area and some buttons that show how an orb can be transformed in how it chooses to display its sub/connected orbs. they should all be equivalent visual presentations of the same thing. arrows, orits, nested within, nested but free moving or spinning, on the outside, invisible to still within via EnterOrb....
    const rainbowOrb1 = OrbFactory.createOrbByType("RAINBOW_NEST", "This is a test");
    rainbowOrb1.setPosition(new THREE.Vector3(-10000, 3000, 3000));
    rainbowOrb1.addToScene(scene);
    // Showcase prompt orb with speed text
    const orb = OrbFactory.createOrbByType("PROMPT_ORB", "This is a test orb");
    orb.setPosition(new THREE.Vector3(-1000, 500, 0));
    orb.addToScene(scene);
    // Enable speed text with WPM override
    orb.enableSpeedText({ wpm: 500 });
    // Toggle on keypress - Review 10/26/2025 - ?????
    window.addEventListener('keydown', (e) => {
        if (e.key === 't') {
            orb.toggleSpeedText({ wpm: 600 });
        }
    });
    registerScenePolicy('development', { floorY: 2 });
}
export const DevelopmentWorld = new World({
    slug: 'development',
    label: 'Development',
    sceneName: 'development',
    build: buildDevelopmentWorld,
});
global.worldRegistry.registerWorld(DevelopmentWorld);
